import { createContext, useContext, ReactNode } from 'react';
import { useAuth, User } from './AuthContext';

export interface TenantContextValue {
  organizationId: string;
  organizationSlug: string;
  organizationName: string;
}

const TenantContext = createContext<TenantContextValue | null>(null);

interface TenantProviderProps {
  children: ReactNode;
}

function extractTenantFromUser(user: User | null): TenantContextValue | null {
  if (!user) return null;
  if (!user.organizationId) return null;

  return {
    organizationId: user.organizationId,
    organizationSlug: user.organizationSlug,
    organizationName: user.organizationName,
  };
}

export function TenantProvider({ children }: TenantProviderProps) {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <>{children}</>;
  }

  const tenant = extractTenantFromUser(user);

  if (!tenant) {
    return <>{children}</>;
  }

  return (
    <TenantContext.Provider value={tenant}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider and requires authenticated user with organization');
  }
  return context;
}

export function useTenantOptional(): TenantContextValue | null {
  return useContext(TenantContext);
}
